import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "library-archival-systems",
  kind: "horizontal",
  title: "Library & Archival Systems",
  shortTitle: "Library Systems",
  status: "draft",
  tier: "we-do",
  seoTitle: "Library & Archival Systems | General Applications",
  seoDescription:
    "Library and archival systems that connect collections, metadata, files, versions, rights, search, and lifecycle control.",
  summary:
    "Library and archival systems that turn files, records, metadata, versions, rights, and relationships into a collection people can preserve, search, understand, and use.",
  image: "/images/blog/reading-a-data-module-code-hero.webp",
  relatedCapabilitySlugs: ["data-search-knowledge"],
  relatedIndustrySlugs: ["enterprise", "military", "media-entertainment"],
  relatedPostSlugs: [
    "search-relevance-taxonomy",
    "reading-a-data-module-code",
    "reconciling-fragmented-enterprise-identifiers",
  ],
  engineering: [
    "Collection, item, file, version, and relationship modeling",
    "Metadata, taxonomy, identifier, provenance, and authority design",
    "Ingest, digitization, validation, derivative, and preservation workflows",
    "Rights, access, retention, lifecycle, and disposition controls",
    "Search, browse, facet, and contextual discovery interfaces",
    "Integration of media, technical publications, records, and archival sources",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "A File Share Is Not a Library",
    },
    {
      type: "paragraph",
      text: "A folder tree can hold files, but it cannot explain which version is authoritative, what an item describes, how it relates to other items, what rights or access rules apply, or whether it may be preserved, replaced, or removed. Library and archival systems turn those decisions into visible records instead of knowledge held by the person who remembers where the file went.",
    },
    {
      type: "heading",
      level: 2,
      text: "Preservation and Discovery Depend on the Same Model",
    },
    {
      type: "paragraph",
      text: "Whether the collection contains media assets, technical data, research material, operational records, or historical documents, its value depends on being able to find an item and understand its context. That requires identifiers, metadata, relationships, lifecycle state, and access rules that survive changes in storage, software, and staff.",
    },
  ],
  phases: {
    study: {
      focus:
        "Identify the collection's materials, users, metadata, authority, preservation obligations, access rules, and discovery tasks before choosing a platform or taxonomy.",
      produces: [
        "Collection, item, file, version, and relationship inventory",
        "Metadata, identifier, provenance, and source-authority assessment",
        "Rights, access, retention, preservation, and lifecycle analysis",
        "Discovery, research, operational-use, and stewardship task map",
      ],
      fit: {
        good: [
          "Organizations with valuable collections spread across file shares, storage platforms, catalogs, technical systems, or individual custodians",
          "Teams that need durable preservation and useful discovery without separating metadata, rights, lifecycle, and access from the files themselves",
        ],
        poor: [
          "A request to migrate files into a new repository without deciding what the files represent, which versions matter, or how people will find and govern them afterward",
          "A collection whose ownership, access authority, or retention obligations cannot yet be established",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the collection model, metadata, authority, lifecycle, and discovery architecture that makes preservation and use reinforce one another.",
      produces: [
        "Collection, item, metadata, identifier, and relationship model",
        "Authority, rights, access, retention, and lifecycle architecture",
        "Ingest, validation, derivative, and preservation workflow design",
        "Search, browse, facet, and contextual-discovery approach",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the records, workflows, integrations, validation, and exception paths needed to operate the collection after its initial migration or build.",
      produces: [
        "Metadata, taxonomy, identifier, and provenance specifications",
        "Ingest, quality-control, versioning, and lifecycle procedures",
        "Search, browse, permission, and access-interface specifications",
        "Integration, migration, audit, and stewardship definitions",
      ],
    },
    implementation: {
      focus:
        "Build or modernize the system around representative collections, then validate that custodians and users can preserve, find, interpret, and govern the material.",
      produces: [
        "Connected collection, metadata, file, and lifecycle records",
        "Ingest, preservation, access, and stewardship workflows",
        "Search and discovery interfaces with source context",
        "Migration, operational guidance, and collection-governance handoff",
      ],
    },
  },
};
